import React, { FC, useMemo } from 'react';
import { T } from 'src/shared/helpers';
import styled from 'styled-components';
import { MeasurementUnit } from '../../../configurator-scene-editor/useWorkspacePreferences';

const Statusbar = styled.footer`
    position: absolute;
    right: 12px;
    bottom: 0;
    left: 12px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    height: 26px;
    padding: 0 8px;
    color: ${(props) => props.theme.app.primary};
    background: ${(props) => props.theme.app.backgroundColor};
    border: 1px solid ${(props) => props.theme.app.borderColor};
    border-radius: 6px;
    font-size: 11px;
`;

const UnitSelect = styled.select`
    height: 20px;
    border: 0;
    color: ${(props) => props.theme.app.textColor};
    background: transparent;
    font: inherit;
`;

const Measurement = styled.span`
    flex: 1;
    text-align: center;
    white-space: nowrap;
`;

const Metrics = styled.div`
    display: flex;
    gap: 12px;
    white-space: nowrap;
`;

interface SceneEditorStatusbarProps {
    sceneEditor?: any;
    measurementUnit: MeasurementUnit;
    onMeasurementUnitChange: (unit: MeasurementUnit) => void;
}

const SceneEditorStatusbar: FC<SceneEditorStatusbarProps> = ({ sceneEditor, measurementUnit, onMeasurementUnitChange }) => {
    const measurements = useMemo(() => {
        const raw = sceneEditor?.modelMeasurements;
        if (!raw) return null;
        const multiplier = { m: 1, cm: 100, mm: 1000, in: 39.3700787 }[measurementUnit];
        return [raw.x, raw.y, raw.z].map((value) => (Number(value) * multiplier).toFixed(2)).join(' × ');
    }, [measurementUnit, sceneEditor]);

    const materialsCount = useMemo(() => {
        try {
            return sceneEditor?.getAllMaterials?.().length ?? 0;
        } catch {
            return 0;
        }
    }, [sceneEditor]);

    return (
        <Statusbar aria-label={T._('Scene statistics', 'Admin')}>
            <UnitSelect
                value={measurementUnit}
                onChange={(event) => onMeasurementUnitChange(event.target.value as MeasurementUnit)}
                aria-label={T._('Measurement unit', 'Admin')}
            >
                <option value='m'>{T._('Meters', 'Admin')}</option>
                <option value='cm'>{T._('Centimeters', 'Admin')}</option>
                <option value='mm'>{T._('Millimeters', 'Admin')}</option>
                <option value='in'>{T._('Inches', 'Admin')}</option>
            </UnitSelect>
            <Measurement>{measurements ? `${measurements} ${measurementUnit}` : '—'}</Measurement>
            <Metrics>
                <span>
                    {T._('Mats', 'Admin')}: {materialsCount}
                </span>
            </Metrics>
        </Statusbar>
    );
};

export default SceneEditorStatusbar;
